import { Component, OnInit, ViewChild } from '@angular/core';
import { ModalDirective, ModalOptions } from 'ngx-bootstrap/modal';
import { FormGroup, Validators, FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router'
import { ToastrService } from 'ngx-toastr';

import { Customer, Product, ProductInstance, Status, ProductInstanceWithStatus, UserStatus } from 'src/api/models';
import { CustomerService, ProductService, ProductInstanceService } from 'src/api/services';
import { ProductInstanceTableKeys, ProductInstanceTableRow } from './customer-specific.types';
import { StatusExtended } from 'src/app/models/status-extended';

interface ProductForm {
  productId: number;
  instanceName: string;
}

@Component({
  selector: 'app-customer-specific',
  templateUrl: './customer-specific.component.html',
  styleUrls: ['./customer-specific.component.scss']
})
export class CustomerSpecificComponent implements OnInit {
  @ViewChild('productInstanceModal', { static: false }) productModal: ModalDirective;

  public customer?: Customer;
  public productInstances?: Array<ProductInstanceWithStatus>;
  public owners?: Array<UserStatus>;
  public activeUsers?: Array<UserStatus>;
  public productInstancesFormatted: Array<ProductInstanceTableRow>;
  public products?: Array<Product>;
  public productAlerts?: Array<Status>;

  public isSubmitting = false;
  public readonly productTableKeys = ProductInstanceTableKeys;
  public readonly productGroupModel: FormGroup = this.formBuilder.group({
    productId: ['', Validators.required],
    instanceName: ['', Validators.required],
  });

  get productGroupValue(): ProductForm {
    return this.productGroupModel.value;
  }

  private modalConfig: ModalOptions = {
    backdrop: true,
    ignoreBackdropClick: true,
    keyboard: false
  }

  constructor(
    private customerService: CustomerService,
    private productService: ProductService,
    private productInstanceService: ProductInstanceService,
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private toasterService: ToastrService) { }

  ngOnInit(): void {
    const customerIdParam = parseInt(this.route.snapshot.paramMap.get("customerId"));
    if (!isNaN(customerIdParam)) {
      this.getCustomerById(customerIdParam)
      this.getProductsInstancesByCustomerId(customerIdParam)
      this.getAllProducts();
    } else {
      this.toasterService.error("Failed To Find Customer", "Customer")
      this.router.navigate(['/customers']);
    }
  }

  getCustomerById(id: number): void {
    this.customerService.CustomerGetCustomerById(id)
      .subscribe(
        res => {
          this.customer = res;
        },
        err => {
          console.error("ERROR: getCustomerById", err);
        }
      )
  }

  getProductsInstancesByCustomerId(id: number): void {
    this.productInstanceService.ProductInstanceGetProductInstancesByCustomerId(id)
      .subscribe(
        res => {
          this.productInstances = res.productInstances;
          this.owners = res.owners;
          this.activeUsers = res.activeUsers;
          this.formatProductInstances(this.productInstances);
          this.formatAlerts(this.productInstances);
        },
        err => {
          console.log("ERROR: getProductsInstancesByCustomerId", err);
        }
      )
  }

  getAllProducts(): void {
    this.productService.ProductGetAllProducts()
      .subscribe(
        res => {
          this.products = res;
        },
        err => {
          console.log("ERROR: getAllProducts", err)
        }
      )
  }

  /**
   * Redirect to specific product view
   * @param product
   */
  selectProductInstance(productInstanceFormatted: ProductInstanceTableRow): void {
    this.router.navigate(['/customers', this.customer.customerId, productInstanceFormatted.productId, productInstanceFormatted.id]);
  }

  /**
   * Formats product instnace data to be used in the table, primarily for status display
   */
  formatProductInstances(productInstances: Array<ProductInstanceWithStatus>): void {
    this.productInstancesFormatted = productInstances.map(productInstance => {
      let highestStatus = productInstance.statusList[0] || {} as Status;
      let alertCount = 0;

      productInstance.statusList.forEach(status => {
        if (status.level < highestStatus.level) {
          highestStatus = status;
        }

        if (status.level < 2) {
          alertCount++;
        }
      })
      let productId = 0;
      if (productInstance.product){
        productId = productInstance.product.productId;
      }

      return {
        id: productInstance.productInstanceId,
        productId: productId,
        instanceName: productInstance.instanceName,
        name: productInstance.product?.productName,
        description: productInstance.product?.description,
        status: {
          level: !isNaN(highestStatus.level) ? highestStatus.level : 2,
          type: 0,
          message: `${alertCount} Alerts`
        }
      };
    })
  }

  /**
   * Combine all product instance status into one array to display in Array component
   * @param customers  
   */
  formatAlerts(products: Array<ProductInstanceWithStatus>): void {
    const alerts = new Array<StatusExtended>();

    products.forEach(product => {
      // alerts.push(...customer.statusList);
      product.statusList.forEach(status => {
        const extended: StatusExtended = {
          level: status.level,
          type: status.type,
          message: `${product.product.productName}'s ${status.message.toLowerCase()}.`,
          url: `product/${product.productInstanceId}`
        }

        alerts.push(extended);
      })
    })

    this.productAlerts = alerts.sort((a, b) => a.level - b.level);
  }

  /**
   * Open the product modal
   */
  openProductInstanceModal(): void {
    this.productModal.config = this.modalConfig;
    this.productModal.show();
  }

  /**
   * Close the product modal, resets create logic.
   */  
  closeProductInstanceModal(): void {
    this.productModal.hide();
    this.productGroupModel.reset();
  }

  /**
   * Create product instance, using the form values, updates products array on success
   */
  createProductInstance(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    
    const args: ProductInstanceService.ProductInstanceCreateProductInstanceParams = {
      productId: this.productGroupValue.productId,
      customerId: this.customer.customerId,
      instanceName: this.productGroupValue.instanceName
    };

    this.productInstanceService.ProductInstanceCreateProductInstance(args)
      .subscribe(
        res => {
          this.getProductsInstancesByCustomerId(this.customer.customerId);
          this.closeProductInstanceModal();
          this.toasterService.success(`Successfully Created Product`, "Create Product")
          this.isSubmitting = false;
        },
        err => {
          console.error("ERROR: createCustomer", err);
          this.toasterService.error(`Failed To Create Product`, "Create Product")
          this.isSubmitting = false;
        }
      )
  }

  /**
   * Delete specific product instance by ID, updates products array on success
   * @param formattedProductInstance
   */
  deleteProductInstance(formattedProductInstance: ProductInstanceTableRow): void {
    this.productInstanceService.ProductInstanceDeleteProductInstanceById(formattedProductInstance.id)
      .subscribe(
        res => {
          this.getProductsInstancesByCustomerId(this.customer.customerId);
          this.toasterService.success(`Successfully Deleted Product: ${formattedProductInstance.name || '-'}`, "Delete Product")
        },
        err => {
          console.error("ERROR: deleteCustomer", err);
          this.toasterService.error(`Failed To Delete Product: ${formattedProductInstance.name || '-'}`, "Delete Product")
        }
      )
  }

  replaceProductInstance(pi: ProductInstanceTableRow): void{
    alert('Update');
  }
}
